import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import classNames from 'classnames';
import { DataRoomNode } from '../../models/DataRoomNode';
import { useTranslation } from 'react-i18next';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import FolderIcon from 'icons/Folder.icon';
import { Button, Loader } from 'ncoded-component-library';
import MymeTypeIcon from '../DataRoomDocumentsRouter/components/MymeTypeIcon';
import ArrowupIcon from 'icons/Arrowup.icon';
import HomeIcon from 'icons/Home.icon';
import TrashIcon from 'icons/Trash.icon';
import StapleIcon from 'icons/Staple.icon';
import dataRoomApi from '../../dataRoomApi';
import { getNodeMimeType } from 'utils';
import { DATA_ROOM_ROOT_FOLDER } from 'data/global';

import './ChooseAFileInDr.styles.scss';

type ChooseAFileInDrProps = {
  className?: string;
  onChange: (val: DataRoomNode | null) => void;
  currenntlySelectedFile: null | DataRoomNode;
  dataRoomId: string;
};

type ChooseAFileInDrRef = {
  setIsEditing: (val: boolean) => void;
};

const ChooseAFileInDr: React.ForwardRefRenderFunction<
  ChooseAFileInDrRef,
  ChooseAFileInDrProps
> = (props, ref) => {
  const { className, currenntlySelectedFile, dataRoomId, onChange } = props;

  const [currentFolderParentChildren, setCurrentFolderParentChildren] =
    useState<Array<DataRoomNode>>([]);
  const [currentParentFileChildren, setCurrentParentFileChildren] = useState<
    Array<DataRoomNode>
  >([]);
  const [areChildrenLoading, setAreChildrenLoading] = useState(true);
  const { t } = useTranslation();
  const { popServerError } = useContext(PopNotificationsContext);
  const [documentRoute, setDocumentRoute] = useState<DataRoomNode[]>([]);

  const [currentParent, setCurrentParent] = useState<string>(
    currenntlySelectedFile?.parentId || '',
  );
  const [isEditing, setIsEditing] = useState(false);

  const classes = classNames('choose-a-file-in-dr', className);

  useImperativeHandle(ref, () => ({ setIsEditing }), []);

  useEffect(() => {
    (async () => {
      try {
        setAreChildrenLoading(true);

        if (!currentParent) {
          setDocumentRoute([]);
        }

        const {
          data: { files, folders },
        } = await dataRoomApi.nodes.getAllParentNodes(
          dataRoomId,
          currentParent,
        );
        setCurrentFolderParentChildren(folders);
        setCurrentParentFileChildren(files);
      } catch (e) {
        popServerError(e);
      } finally {
        setAreChildrenLoading(false);
      }
    })();
  }, [currentParent, dataRoomId, popServerError]);

  useEffect(() => {
    (async () => {
      try {
        setAreChildrenLoading(true);

        if (!currentParent) {
          setDocumentRoute([]);
          return;
        }

        const { data } = await dataRoomApi.nodes.getFolderPath(
          dataRoomId,
          currentParent,
        );

        setDocumentRoute(data.reverse());
      } catch (e) {
        popServerError(e);
      } finally {
        setAreChildrenLoading(false);
      }
    })();
  }, [currenntlySelectedFile, currentParent, dataRoomId, popServerError]);

  return (
    <div className={classes}>
      {isEditing ? (
        <>
          <ul className="choose-a-file-in-dr__breadcrumps">
            <li
              onClick={() => {
                setAreChildrenLoading(true);
                setDocumentRoute([]);
                setCurrentParent(null);
              }}
            >
              <HomeIcon />
              {t(DATA_ROOM_ROOT_FOLDER)}
            </li>
            {documentRoute.map((node) => (
              <li key={node._id} onClick={() => setCurrentParent(node._id)}>
                <p>{node.name}</p>
              </li>
            ))}
          </ul>
          {!!currentFolderParentChildren?.length && (
            <ul className="choose-a-file-in-dr__folders">
              {currentFolderParentChildren.map((node) => (
                <li key={node._id} onClick={() => setCurrentParent(node._id)}>
                  <FolderIcon className="folder-icon" />
                  <p>{node.name}</p>
                </li>
              ))}
            </ul>
          )}

          {!!currentParentFileChildren.length && (
            <ul className="choose-a-file-in-dr__files">
              {currentParentFileChildren.map((node) => (
                <li
                  key={node._id}
                  onClick={() => {
                    onChange(node);
                    setIsEditing(false);
                  }}
                >
                  <MymeTypeIcon mimeType={getNodeMimeType(node)} />
                  <p>{node.name}</p>
                </li>
              ))}
            </ul>
          )}
          <div className="choose-a-file-in-dr__actions">
            {!!documentRoute.length && (
              <Button
                icon={ArrowupIcon}
                variant="link"
                className="choose-a-file-in-dr__actions__back-up"
                onClick={() => {
                  if (documentRoute.length > 1) {
                    setCurrentParent(
                      documentRoute[documentRoute.length - 2]._id,
                    );
                  } else {
                    setCurrentParent(null);
                  }
                }}
              >
                {t('backUpALevel')}
              </Button>
            )}
            {currenntlySelectedFile && (
              <Button
                onClick={() => {
                  onChange(null);
                  setIsEditing(false);
                }}
                variant="outline"
              >
                {t('removeRelatedFile')}
              </Button>
            )}
          </div>
          {areChildrenLoading && <Loader />}
        </>
      ) : (
        <div className="choose-a-file-in-dr__not-in-edit">
          {currenntlySelectedFile ? (
            <div className="choose-a-file-in-dr__not-in-edit__selected">
              <p>{t('relatesToThisSpecific')}</p>
              <div>
                <MymeTypeIcon
                  mimeType={getNodeMimeType(currenntlySelectedFile)}
                />
                <span>{currenntlySelectedFile.name}</span>
                <Button
                  variant="icon"
                  className="warning-action-button"
                  icon={TrashIcon}
                  onClick={() => {
                    onChange(null);
                  }}
                />
              </div>
            </div>
          ) : (
            <div className="choose-a-file-in-dr__not-in-edit__no-selected">
              <span>{t('noFileRelated')}</span>
              <Button onClick={() => setIsEditing(true)} variant="outline">
                <StapleIcon />
                {t('relateFileToQuestion')}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default forwardRef(ChooseAFileInDr);
